import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import ThemeToggle from "../common/themeToggle";
import LanguageSelector from "../lang/langselector";

// Tailwind sınıfları statik kalmalı; eşik başına hazır sınıf kümeleri.
const BREAKPOINTS = {
  sm: {
    desktop: "hidden sm:flex",
    mobile: "sm:hidden",
    langDesktop: "hidden sm:block",
  },
  md: {
    desktop: "hidden md:flex",
    mobile: "md:hidden",
    langDesktop: "hidden md:block",
  },
  lg: {
    desktop: "hidden lg:flex",
    mobile: "lg:hidden",
    langDesktop: "hidden lg:block",
  },
};

const linkClass = (current, block) =>
  `${block ? "block w-full text-left" : ""} rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap ${
    current
      ? "bg-[--gr-4] text-white"
      : "text-[--gr-7] hover:bg-[--gr-4] hover:text-white"
  }`;

const NavItem = ({ item, block = false }) => {
  if (item.to !== undefined) {
    return (
      <Link
        to={item.to}
        className={linkClass(item.current, block)}
        aria-current={item.current ? "page" : undefined}
      >
        {item.name}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={item.onClick}
      className={`${linkClass(item.current, block)} bg-transparent`}
      aria-pressed={item.current || undefined}
    >
      {item.name}
    </button>
  );
};

/**
 * Uygulamanın tek üst çubuğu.
 *
 * - links: [{ name, to | onClick, current }] masaüstünde ortada, mobilde açılır panelde
 * - subtitle: logonun yanındaki küçük metin (md ve üstünde görünür)
 * - actions: masaüstü sağ alandaki ögeler; mobileActions: mobilde başlıkta kalanlar
 * - panelExtra: mobil panelin altına eklenen ögeler (ör. çıkış düğmesi)
 * - breakpoint: bağlantıların satıra alındığı eşik (sm | md | lg)
 * - bg / className: arka plan ve ek sarmalayıcı sınıfları
 */
const AppHeader = ({
  subtitle,
  links = [],
  actions,
  mobileActions,
  panelExtra,
  breakpoint = "lg",
  bg = "bg-[--nav-bg-1]",
  className = "relative z-40",
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const bp = BREAKPOINTS[breakpoint] || BREAKPOINTS.lg;
  const hasPanel = links.length > 0 || Boolean(panelExtra);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className={`w-full ${bg} ${className}`}>
      <div className="px-[4%] h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {hasPanel && (
            <button
              type="button"
              className={`${bp.mobile} inline-flex items-center justify-center rounded-md p-2 -ml-2 text-[--gr-5] hover:bg-[--bg-2] hover:text-white bg-transparent`}
              onClick={() => setOpen((prev) => !prev)}
              aria-expanded={open}
              aria-controls="app-header-panel"
            >
              <span className="sr-only">
                {open ? t("nav.closeMenu") : t("nav.openMenu")}
              </span>
              {open ? (
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          )}

          <Link
            to="/"
            className="text-2xl font-semibold font-brand text-[--tx-5] whitespace-nowrap leading-none"
            aria-label="LiwaSoft"
          >
            Liwa
            <span className="text-[--tx-6] font-brand">Soft</span>
          </Link>

          {subtitle && (
            <div className="hidden md:block text-sm text-[--tx-2] truncate Poppins">
              {subtitle}
            </div>
          )}

          {links.length > 0 && (
            <nav
              className={`${bp.desktop} items-center gap-1 ml-2`}
              aria-label={t("nav.mainMenu")}
            >
              {links.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {mobileActions && (
            <div className={`${bp.mobile} flex items-center gap-2`}>
              {mobileActions}
            </div>
          )}
          {actions && (
            <div className={`${bp.desktop} items-center gap-2`}>{actions}</div>
          )}
          <ThemeToggle />
          <LanguageSelector className={hasPanel ? bp.langDesktop : ""} />
        </div>
      </div>

      {hasPanel && open && (
        <>
          <div
            className={`${bp.mobile} fixed inset-0 top-16 bg-black/30 z-30`}
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            id="app-header-panel"
            className={`${bp.mobile} absolute left-0 right-0 top-16 z-40 ${bg} shadow-lg border-0 border-t border-solid border-[--gr-3]`}
          >
            <ul
              className="px-[4%] py-3 flex flex-col gap-1"
              onClick={() => setOpen(false)}
            >
              {links.map((item) => (
                <li key={item.name}>
                  <NavItem item={item} block />
                </li>
              ))}
              <li
                className={`flex items-center justify-between gap-3 ${
                  links.length > 0
                    ? "mt-2 pt-2 border-0 border-t border-solid border-[--gr-3]"
                    : ""
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div>{panelExtra}</div>
                <LanguageSelector />
              </li>
            </ul>
          </div>
        </>
      )}
    </header>
  );
};

export default AppHeader;
